import { ReactElement, useEffect, useRef, useState } from "react";
import _ from "lodash";
import { useFetcher } from "react-router-dom";

import Card from "components/Card";
import FormWithValidation from "components/FormWithValidation";
import Spinner from "components/Spinner";
import SubmitButton from "components/SubmitButton";
import Tabs, { Tab } from "components/Tab";

import CardNumberInput, { CardType } from "components/CreditCardInputs/CardNumberInput";
import CardExpiryInput from "components/CreditCardInputs/CardExpiryInput";
import CVVInput from "components/CreditCardInputs/CVVInput";
import NameOnCardInput from "components/CreditCardInputs/NameOnCardInput";
import PrimaryCardCheckbox from "components/CreditCardInputs/PrimaryCardCheckbox";

import { CardPaymentSource, PaymentSource } from "types/billing/PaymentSource";
import PayPalComponents from "v2/components/billing/modals/upgrade-modal/PayPalComponents";

import { RouteErrors } from "routes/data-routes/types";
import { PaymentSourceActions, PaymentSourceInputName } from "v2/dataroutes/BillingData";
import { PAYMENT_SOURCES_FETCHER_HREF, usePaymentSources } from "v2/pages/billing/hooks/usePaymentSources";
import useBillingInfo from "v2/hooks/billing/useBillingInfo";
import { useCallbackAfterSubmission } from "v2/hooks/useCallbackAfterSubmission";
import { useToasterFromActionData } from "hooks/useToasterFromActionData";
import { trackPaymentMethodAddSubmitted, trackPaymentMethodAddSucceeded } from "utils/Tracking";


export default function PaymentSourceForm(
  {
    paymentSource,
    secondaryButton,
    onSuccess,
  }: {
    paymentSource?: PaymentSource,
    secondaryButton?: (handleClearInputs: () => void) => ReactElement,
    onSuccess?: () => void,
  }
) {

  const { paymentSources, paymentSourcesFetcher, paymentSourcesError } = usePaymentSources();
  const isOnlyPaymentMethod = !paymentSources || paymentSources.length === 0;

  // paymentSourcesFetcher is shared with siblings (set-primary, remove, edit forms),
  // so the success callback fires for any of their actions too. This ref attributes
  // add_succeeded to this form's own add submit only.
  const cardAddSubmittedRef = useRef(false);

  useCallbackAfterSubmission(() => {
    if (cardAddSubmittedRef.current) {
      trackPaymentMethodAddSucceeded('card');
      cardAddSubmittedRef.current = false;
    }
    onSuccess?.();
  }, paymentSourcesFetcher);

  // Clear the ref on a failed-submit idle transition so it doesn't carry into the next
  // unrelated success.
  const prevFetcherStateRef = useRef(paymentSourcesFetcher.state);
  useEffect(() => {
    const prev = prevFetcherStateRef.current;
    const curr = paymentSourcesFetcher.state;
    prevFetcherStateRef.current = curr;
    if (prev === "submitting" && curr === "idle" && paymentSourcesError && cardAddSubmittedRef.current) {
      cardAddSubmittedRef.current = false;
    }
  }, [paymentSourcesFetcher.state, paymentSourcesError]);
  const [ selectedTabIdx, setSelectedTabIdx ] = useState(0);
  const [ clearInputsKey, setClearInputsKey ] = useState(0);
  const [ cardType, setCardType ] = useState<CardType>('other');
  const handleClearInputs = () => {
    setClearInputsKey(k => k + 1);
    setCardType('other');
  };
  const isEditMode = paymentSource !== undefined;
  const isPayPalSelected = selectedTabIdx === 1 || paymentSource?.type === "paypal";
  const [ isDirty, setIsDirty ] = useState(false);

  // Separate fetcher for PayPal — avoids revalidation conflict with the shared payment sources fetcher
  const paypalFetcher = useFetcher({ key: "add-paypal-payment-source" });
  useToasterFromActionData(paypalFetcher);

  // After PayPal action completes successfully, refresh the payment sources list
  const [ paypalSubmitted, setPaypalSubmitted ] = useState(false);
  useEffect(() => {
    if (paypalSubmitted && paypalFetcher.state === "idle") {
      setPaypalSubmitted(false);
      if (!paypalFetcher.data?.error) {
        trackPaymentMethodAddSucceeded('paypal');
        paymentSourcesFetcher.load(PAYMENT_SOURCES_FETCHER_HREF);
        onSuccess?.();
      }
    }
  // onSuccess intentionally excluded — inline callbacks create new references on every render causing fetch loops
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ paypalSubmitted, paypalFetcher.state, paypalFetcher.data, paymentSourcesFetcher ]);

  const handlePayPalApprove = (token: string | undefined) => {
    if (!token) return;

    trackPaymentMethodAddSubmitted('paypal');
    setPaypalSubmitted(true);
    paypalFetcher.submit(
      {
        [PaymentSourceInputName.paypal.billingAgreementToken]: token,
        [PaymentSourceInputName.action]: PaymentSourceActions.add_paypal.value,
      },
      { method: "POST", action: PAYMENT_SOURCES_FETCHER_HREF }
    );
  };

  return (
    <Card
      className="w-full max-w-[564px] px-2 bg-white"
    >
      <FormWithValidation
        fetcher={ paymentSourcesFetcher }
        method="POST"
        action={ PAYMENT_SOURCES_FETCHER_HREF }
        onChange={ () => { if (!isDirty) setIsDirty(true); } }
      >
        <input type="hidden" name={ PaymentSourceInputName.id } value={ paymentSource?.id ?? "" } />
        <input type="hidden" name={ PaymentSourceInputName.type } value={ paymentSource?.type ?? (selectedTabIdx === 0 ? "card" : "paypal") } />

        { (paymentSource === undefined) && (
          <Tabs
            align="center"
            className="!w-full border-b border-b-neutral-200"
            containerClassName="!border-b-0"
            selectedIndex={ selectedTabIdx }
            onChange={ setSelectedTabIdx }
          >
            <Tab title="Credit Card" unmount={ true }>
              <CreditCardInputs
                formErrors={ paymentSourcesError }
                resetKey={ clearInputsKey }
                isOnlyPaymentMethod={ isOnlyPaymentMethod }
                cardType={ cardType }
                onCardTypeChange={ setCardType }
              />
            </Tab>
            <Tab title="PayPal" unmount={ true }>
              <PayPalInputs formErrors={ paymentSourcesError } onApprove={ handlePayPalApprove } fetcher={ paypalFetcher } />
            </Tab>
          </Tabs>
        ) }

        { (paymentSource?.type === "card") && (
          <CreditCardInputs paymentSource={ paymentSource } formErrors={ paymentSourcesError } />
        ) }

        { (paymentSource?.type === "paypal") && (
          <PayPalInputs formErrors={ paymentSourcesError } onApprove={ handlePayPalApprove } fetcher={ paypalFetcher } />
        ) }

        { isPayPalSelected ? (
          secondaryButton && (
            <div className="flex flex-col gap-y-4">
              { secondaryButton(handleClearInputs) }
            </div>
          )
        ) : (
          <div className="flex flex-col gap-y-4 pt-4">
            <SubmitButton
              text={ isEditMode ? "Update" : "Add" }
              className="button button-primary w-full"
              checkFormValidity
              fetchersToCheck={ [ paymentSourcesFetcher ] }
              disabled={ isEditMode && !isDirty }
              onClick={ () => {
                if (!isEditMode) {
                  trackPaymentMethodAddSubmitted('card');
                  cardAddSubmittedRef.current = true;
                }
              } }
              { ...PaymentSourceActions.add_or_update }
            />
            { secondaryButton?.(handleClearInputs) }
          </div>
        ) }
      </FormWithValidation>
    </Card>
  );
};


function CreditCardInputs(
  {
    paymentSource,
    formErrors,
    resetKey,
    isOnlyPaymentMethod,
    cardType = 'other',
    onCardTypeChange,
  }: {
    paymentSource?: CardPaymentSource,
    formErrors?: RouteErrors.ActionError,
    resetKey?: number,
    isOnlyPaymentMethod?: boolean,
    cardType?: CardType,
    onCardTypeChange?: (cardType: CardType) => void,
  }
) {
  const patchExpiryErrorMessages = (formInputErrors?: RouteErrors.FormInputErrors) => {
    if (formInputErrors?.["expiry_month"]?.startsWith("It should")) {
      formInputErrors["expiry_month"] = "Month " + formInputErrors["expiry_month"].slice(3);
    }

    if (formInputErrors?.["expiry_year"]?.startsWith("It should be")) {
      formInputErrors["expiry_year"] = "Year " + formInputErrors["expiry_year"].slice(3);
    }

    return formInputErrors;
  }

  const [ formSubmissionErrors, setFormSubmissionErrors ] = useState(patchExpiryErrorMessages(formErrors?.formInputErrors));
  useEffect(() => {
    setFormSubmissionErrors(patchExpiryErrorMessages(formErrors?.formInputErrors));
  }, [ formErrors ]);

  // in 'update' mode we can only adjust the expiry date and the name on card
  const addNewMode = paymentSource === undefined;
  const [ makePrimary, setMakePrimary ] = useState(true);

  // Wire-transfer users: a card is only ever a backup, so frame "default" as "primary backup".
  const { hasWireTransferOnAccount } = useBillingInfo();

  // Andrew Comment - i think we should discuss how we want to do regarding updating payment details.
  // initially i did it in a way where the all the field were clear.
  // then when the user wants to update their payment details, i removed their original one in the backend -
  // - and then created a new one with the new details when the user clicked the update button.
  // it depends on what we want the user to do i guess?
  // but also considering edge cases and

  return (
    <div className="flex flex-col gap-y-2 pt-2">
      { addNewMode && (
        <CardNumberInput
          errorMessage={ formSubmissionErrors?.["number"] }
          onChange={ (event) => {
            event.currentTarget.setCustomValidity("");
            setFormSubmissionErrors(errors => _.omit(errors, "number"));
          } }
          onCardTypeChange={ onCardTypeChange }
          isValidated={ formSubmissionErrors?.["number"] !== undefined }
          resetKey={ resetKey }
        />
      ) }

      <div className="flex flex-row gap-x-6">
        <div className="flex-1">
          <CardExpiryInput
            errorMessage={ formSubmissionErrors?.["expiry_month"] ?? formSubmissionErrors?.["expiry_year"] }
            onChange={ (event) => {
              event.currentTarget.setCustomValidity("");
              setFormSubmissionErrors(errors => _.omit(errors, "expiry_month", "expiry_year"));
            } }
            value={ paymentSource?.expiry ? (String(paymentSource.expiry.month).padStart(2, "0") + String(paymentSource.expiry.year % 100).padStart(2, "0")) : undefined }
            resetKey={ resetKey }
          />
        </div>
        <div className="flex-1">
          { addNewMode && (
            <CVVInput resetKey={ resetKey } cardType={ cardType } />
          ) }
        </div>
      </div>

      <NameOnCardInput
        value={ paymentSource?.nameOnCard ?? "" }
        resetKey={ resetKey }
      />

      { addNewMode && (
        <label className="flex items-center gap-x-2 text-sm text-gray dark:text-neutral-600 select-none cursor-pointer">
          <PrimaryCardCheckbox
            pure
            checked={ makePrimary }
            onChange={ () => setMakePrimary(prev => !prev) }
            value={ makePrimary ? "true" : "" }
            disabled={ isOnlyPaymentMethod }
          />
          { hasWireTransferOnAccount ? "Make this my primary backup card" : "Make this my default payment method" }
        </label>
      ) }
    </div>
  );
}

function PayPalInputs(
  {
    onApprove,
    fetcher,
  }: {
    formErrors?: RouteErrors.ActionError,
    onApprove: (token: string | undefined) => void,
    fetcher: ReturnType<typeof useFetcher>,
  }
) {
  const { hasPayPalSource } = usePaymentSources();
  const isSubmitting = fetcher.state !== "idle";

  if (hasPayPalSource) {
    return (
      <div className="flex flex-col items-center gap-y-2 py-4">
        <p className="text-sm text-gray-600">A PayPal payment method is already linked to your account.</p>
      </div>
    );
  }

  if (isSubmitting) {
    return (
      <div className="flex flex-col items-center gap-y-3 py-6">
        <Spinner size="MD" />
        <p className="text-sm text-gray-600">Adding PayPal payment method...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-2 pt-2">
      <PayPalComponents
        validate={ false }
        onApproveBillingAgreement={ onApprove }
      />
    </div>
  );
}
